import * as React from 'react';
import { FlowsBrowser } from '../Components/Flows/FlowsBrowser';
import { FlowKanban } from '../Components/Flows/FlowKanban';

import type { User, UserScope } from '../../MedicareProAPI/Entities/User/User';
import { UserFlowRepository } from '../../MedicareProAPI/Repositories/User/UserFlowRepository';
import type { UserFlow, FlowBundlePayload } from '../../MedicareProAPI/Entities/User/UserFlow';
import { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import { UserFlowStageRepository } from '../../MedicareProAPI/Repositories/User/UserFlowStageRepository';
import { IndCollection } from '../../MedicareProAPI/Collections/Ind/IndCollection';
import { IndFlowAssignmentCollection } from '../../MedicareProAPI/Collections/Ind/IndFlowAssignmentCollection';
import type { IndFlowAssignment } from '../../MedicareProAPI/Entities/Ind/IndFlowAssignment';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import { FlowKanbanEditor } from '../Components/Flows/FlowKanbanEditor';
import { BS_COLOR_NAMES, BS_HEX } from '../Constants/Colors';
import { SIDETAB_POSITION, SideTab, SIDETAB_SIZE } from '../Components/SideTab';
import StyledModal from '../Components/StyledModal';
import { WebAPI } from '../../MedicareProAPI';

interface AgentFlowsPageProps {
  userAccessToken: string;
  userId: number;
  usersAgencyId: number | null;
  directUplineId: number | null;
  absoluteUplineId: number | null;
  staffId: number | null;
  communityAgent: boolean;
  operatorName?: string;
}

type StageResponse = {
  individuals: IndFlowAssignment[];
  offset: number;
  limit: number;
  total: number;
};

/**
 * Panel for managing Templates
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */

export default function AgentFlowsPage({
  userId,
  userAccessToken,
  usersAgencyId,
  directUplineId,
  absoluteUplineId,
  communityAgent,
  staffId,
  operatorName,
}: AgentFlowsPageProps): React.JSX.Element {
  const userRepository = new UserRepository();
  userRepository.setAccessToken(userAccessToken);

  const userFlowRepository = new UserFlowRepository();
  userFlowRepository.setAccessToken(userAccessToken);

  const userFlowStageRepository = new UserFlowStageRepository();
  userFlowStageRepository.setAccessToken(userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userAccessToken);

  const [user, setUser] = React.useState<User>(null);
  const [flows, setFlows] = React.useState<UserFlow[]>([]);
  const [selectedFlow, setSelectedFlow] = React.useState<UserFlow | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  const [folderOptions, setFolderOptions] = React.useState<string[]>([]);
  const [isEditingFlow, setIsEditingFlow] = React.useState<boolean>(false);
  const [isMakingNewFlow, setIsMakingNewFlow] = React.useState<boolean>(false);

  const [showFlowModel, setShowFlowModal] = React.useState<boolean>(false);

  const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
  const [deleting, setDeleting] = React.useState<boolean>(false);

  const [newFlowNonce, setNewFlowNonce] = React.useState(0);
  const [userScope, setUserScope] = React.useState<UserScope>();

  const startNewFlow = () => {
    setIsMakingNewFlow(true);
    setSelectedFlow(null);
    setNewFlowNonce((n) => n + 1);
  };

  const uniqFlowsById = (items: any[]) => {
    const seen = new Set<number | string>();
    const out: any[] = [];
    for (const f of items) {
      const id = f?.getId?.() ?? f?.id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      out.push(f);
    }
    return out;
  };

  const fetchUserFlows = async (uId: number, criteria: object = {}) => {
    const res = await userFlowRepository.getUserFlows(uId, {
      include: ['flowStages', 'user'],
      criteria: criteria,
    });
    return res?.getItems?.() ?? [];
  };

  const fetchData = async (targetUser: number) => {
    setLoading(true);
    try {
      // get scope first
      const scope = await webAPI.getUserScope(targetUser);
      const isCommunity = String(scope?.userSubTypeDesignation || '').toLowerCase() === 'community';
      const ownerId = scope?.ownerId;
      setUserScope(scope);
      userRepository
        .getUser(userId, {
          include: ['letters', 'signature', 'signatureEmail', 'signaturePic', 'companyLogo'],
        })
        .then((user) => {
          setUser(user);
        });

      const promises: Promise<any[]>[] = [fetchUserFlows(targetUser)];
      if (isCommunity && ownerId && ownerId !== targetUser) {
        const criteria = { loaAccess: true };
        promises.push(fetchUserFlows(ownerId, criteria));
      }

      const [selfFlows, ownerFlows = []] = await Promise.all(promises);

      // merge, keeping user's own flows first in order
      const merged = uniqFlowsById([...(selfFlows || []), ...(ownerFlows || [])]);

      setFlows(merged);

      // selection rules:
      // 1) keep current selection if it still exists
      // 2) else prefer first of the authorized user's own flows
      // 3) else fallback to first merged
      setSelectedFlow((prev) => {
        const prevId = prev?.getId?.();
        if (prevId && merged.some((f) => (f.getId?.() ?? f.id) === prevId)) return prev;
        if ((selfFlows?.length ?? 0) > 0) return selfFlows[0];
        return merged[0] ?? null;
      });
    } catch (e) {
      console.error('Failed to load flows', e);
      setFlows([]);
      setSelectedFlow(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!userId) return;
    fetchData(userId);
  }, [userId, userAccessToken]);

  const fetchIndsByStage = async (
    flowId: number,
    flowStageId: number,
    offset: number = 0,
    limit: number = 15,
    sortBy: string = 'indLastname',
    order: string = 'ASC',
    criteria: any = {}
  ): Promise<StageResponse> => {
    const response = await userFlowStageRepository.getUserFlowStageInds(flowId, flowStageId, {
      offset: offset,
      limit: limit,
      sortBy: sortBy,
      order: order,
      criteria: criteria,
    });

    return {
      individuals: response.getItems(),
      offset: response.getOffset(),
      limit: response.getLimit(),
      total: response.getTotal(),
    };
  };

  const handleDeleteFlow = (flowId: number) => {
    setDeleteModal(true);
  };

  const deleteFlow = async (flowId: number) => {
    setDeleting(true);
    await userFlowRepository.removeUserFlow(flowId);
    setDeleting(false);
    setDeleteModal(false);
    setFlows((prev) => {
      // where was the deleted flow?
      const idx = prev.findIndex((f) => f.getId() === flowId);
      const nextList = prev.filter((f) => f.getId() !== flowId);

      // If the deleted flow was selected, choose a neighbor to select:
      setSelectedFlow((currSelected) => {
        // if we didn’t delete the selected one, keep current selection as-is (if it still exists)
        if (currSelected && currSelected.getId() !== flowId) {
          const stillExists = nextList.some((f) => f.getId() === currSelected.getId());
          return stillExists ? currSelected : (nextList[0] ?? null);
        }

        // we deleted the selectd one, pick the item that moved into its place
        if (nextList.length === 0) return null;
        const nextIdx = Math.min(idx, nextList.length - 1);
        return nextList[nextIdx];
      });

      return nextList;
    });
  };

  const upsert = async (bundle: FlowBundlePayload) => {
    let flowId = bundle.flowId;

    // 1) Create flow first if new (two-step, quick win)
    if (!flowId) {
      const created = await userFlowRepository.addUserFlow({ name: bundle.flowName });
      flowId = created.getId();
    }

    // 2) Save the bundle against the target flowId
    const saved = await userFlowRepository.saveFlowBundle(flowId, {
      flowId,
      ...bundle,
    });

    // 3) Reconcile UI state immutably
    setFlows((prev) => {
      const list = prev ?? [];
      const idx = list.findIndex((f) => f.getId() === saved.getId());
      if (idx === -1) return [...list, saved];
      const next = list.slice();
      next[idx] = saved;
      return next;
    });

    setSelectedFlow(saved);
    setIsMakingNewFlow(false);
  };

  const handleClose = () => {
    if (isEditingFlow) setIsEditingFlow(false);
    if (isMakingNewFlow) setIsMakingNewFlow(false);
  };

  const isOpen = isEditingFlow || isMakingNewFlow;
  const mode: 'edit' | 'new' = isEditingFlow ? 'edit' : 'new';

  // initial data per mode
  const initialFlowName = mode === 'edit' ? (selectedFlow?.getName() ?? '') : '';
  const initialStages =
    mode === 'edit'
      ? (selectedFlow
          ?.getFlowStages()
          .getItems()
          ?.map((s, i) => ({
            id: s.getId(),
            _tmpId: String(s.getId() ?? i),
            name: s.getName(),
            color: s.getColor(),
            icon: s.getIcon(),
            sortOrder: s.getSortOrder() ?? i,
          })) ?? [])
      : [];

  return (
    <>
      <div className="row contactCard">
        <div className="col-12  px-2  sm-resp">
          <div className="card cardAddress" style={{ height: 'auto', overflow: 'hidden' }}>
            <div className="card-body" id="rxCards" style={{ overflow: 'hidden' }}>
              <div id="letterFormReturn" className="mt-3">
                <div className="row">
                  <div className="col-12 col-md-3 col-lg-2 border-right">
                    {flows && (
                      <FlowsBrowser
                        flows={flows}
                        selectedFlowId={selectedFlow?.getId()}
                        onNewFlow={startNewFlow}
                        onSelect={(flow: UserFlow) => setSelectedFlow(flow)}
                        currentUserId={Number(userId)}
                      />
                    )}
                  </div>
                  <div className="col-12 col-md-3 col-lg-10" style={{ overflow: 'auto' }}>
                    {selectedFlow && (
                      <FlowKanban
                        user={user}
                        userId={userId}
                        userAccessToken={userAccessToken}
                        operatorName={operatorName}
                        userFlow={selectedFlow}
                        fetchIndsByStage={fetchIndsByStage}
                        usersAgencyId={usersAgencyId}
                        directUplineId={directUplineId}
                        absoluteUplineId={absoluteUplineId}
                        staffId={staffId}
                        onUpsert={upsert}
                        onEdit={() => setIsEditingFlow(true)}
                      />
                    )}
                    {!loading && flows.length === 0 && (
                      <div className="ai-suggestions mb-3">
                        <h5 className="font-weight-bold">Flows</h5>
                        <p className="mt-2">
                          Flows are a modern take on the classic Kanban-style organization board,
                          built to help you manage people through repeatable processes—without
                          losing track of what’s next.
                        </p>

                        <p>
                          Instead of juggling spreadsheets, sticky notes, or scattered lists, Flows
                          give you a clear visual system for organizing individuals into logical
                          stages (or “cadences”) that match how you actually work. All you have to
                          do is drag and drop.
                        </p>
                        <p>
                          <h6 className="font-weight-bold">What can Flows be used for?</h6>
                        </p>
                        <p className="mt-2">
                          Flows are flexible by design. You can use them for just about anything
                          that involves moving individuals through a sequence of steps, such as:
                        </p>
                        <ul>
                          <li className="font-weight-bold">Sales pipelines</li>
                          <div className="mb-2">
                            Track prospects from <i>New Lead → Contacted → Quoted → Enrolled.</i>
                          </div>

                          <li className="font-weight-bold">AEP client tracking</li>
                          <div className="mb-2">
                            Stay organized during busy seasons by moving clients through stages
                            like 
                            <i>Needs Review → Plan Options Sent → Ready to Enroll → Completed.</i>
                          </div>

                          <li className="font-weight-bold">Ongoing client service workflows</li>
                          <div className="mb-2">
                            Handle service requests in a structured way 
                            <i>(e.g., Requested → In Progress → Waiting on Client → Resolved).</i>
                          </div>

                          <li className="font-weight-bold">Any custom cadence your team uses</li>
                          <div className="mb-2">
                            If you have a process, you can build a Flow around it.
                          </div>
                        </ul>

                        <div>
                          <i className="far fa-book mr-2"></i>Learn more about Flows by visiting our{' '}
                          <a
                            target="_blank"
                            href="https://help.medicareproapp.com/en/categories/2067841-flows"
                            rel="noreferrer"
                          >
                            Help Guides
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*Edit Flow */}
      <SideTab
        size="mg"
        position={SIDETAB_POSITION.RIGHT}
        title={''}
        show={isOpen}
        onClose={handleClose}
      >
        <FlowKanbanEditor
          key={`flow-editor-${selectedFlow?.getId?.() ?? 'new'}-${newFlowNonce}`}
          userFlow={mode === 'edit' ? selectedFlow : undefined}
          initialFlowName={initialFlowName}
          initialStages={initialStages}
          colorOptions={BS_COLOR_NAMES}
          colorHexMap={BS_HEX}
          iconPrefix="fal"
          onUpsert={upsert}
          onCancel={handleClose}
          onDeleteFlow={handleDeleteFlow}
          mode={mode}
          userScope={userScope}
        />
      </SideTab>

      {/*Delete flow */}
      <StyledModal
        size="md"
        title="Delete Flow"
        footerCloseButton={true}
        footerButtons={
          <button
            onClick={(e) => deleteFlow(selectedFlow.getId())}
            className="btn btn-danger"
            type="button"
          >
            {deleting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Deleting
              </>
            ) : (
              <>Delete</>
            )}
          </button>
        }
        show={deleteModal}
        onClose={(e) => setDeleteModal(false)}
      >
        <div className="row pl-3 pr-3">
          <p>Are you sure you want to delete this flow? This cannot be undone.</p>
          <p>
            {' '}
            <b>NOTE:</b> If you have automations that use this flow you will want to delete those
            automation actions first.
          </p>
        </div>
      </StyledModal>
    </>
  );
}
